<template>
  <div class="dialog-mask" @click="handleMaskClick">
    <div class="dialog-container" @click.stop>
      <div class="dialog-title">{{ title }}</div>
      <div class="dialog-content" v-html="content"></div>
      <!-- 按钮区域：适配单/双按钮布局 -->
      <div class="dialog-btn-group">
        <!-- 取消按钮：动态显示 -->
        <button
            v-if="showCancel"
            class="dialog-btn cancel-btn"
            @click="handleCancel"
        >
          {{ cancelText }}
        </button>
        <!-- 确认按钮 -->
        <button
            class="dialog-btn confirm-btn"
            @click="handleConfirm"
            :disabled="isConfirming"
        >
          {{ isConfirming ? '处理中...' : confirmText }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
// 可选：引入XSS过滤（之前提的安全优化）
// import xss from 'xss';

export default {
  name: 'Dialog',
  props: {
    title: {
      type: String,
      default: '提示'
    },
    content: {
      type: String,
      required: true
    },
    confirmText: {
      type: String,
      default: '确定'
    },
    // 新增：取消按钮文字（默认“取消”）
    cancelText: {
      type: String,
      default: '取消'
    },
    // 新增：是否显示取消按钮（默认隐藏）
    showCancel: {
      type: Boolean,
      default: false
    },
    closeOnMask: {
      type: Boolean,
      default: false
    },
    jumpTo: {
      type: String,
      default: ''
    },
    // 新增：取消后是否跳转（可选，按需添加）
    cancelJumpTo: {
      type: String,
      default: ''
    },
    jumpDelay: {
      type: Number,
      default: 500
    }
  },
  data() {
    return {
      isConfirming: false // 防止重复点击
    };
  },
  // 可选：XSS过滤计算属性
  // computed: {
  //   safeContent() {
  //     return this.content ? xss(this.content) : '';
  //   }
  // },
  methods: {
    handleConfirm() {
      if (this.isConfirming) return;
      this.isConfirming = true;
      this.$emit('confirm'); // 触发确认事件
      if (this.jumpTo) {
        setTimeout(() => {
          this.$router?.replace(this.jumpTo);
          this.isConfirming = false;
        }, this.jumpDelay);
      } else {
        this.isConfirming = false;
      }
    },
    // 新增：取消按钮逻辑
    handleCancel() {
      this.$emit('cancel'); // 触发取消事件
      if (this.cancelJumpTo) { // 取消后跳转（可选）
        setTimeout(() => {
          this.$router?.replace(this.cancelJumpTo);
        }, this.jumpDelay);
      }
    },
    handleMaskClick() {
      if (this.closeOnMask) {
        this.$emit('close');
      }
    }
  }
};
</script>

<style scoped>
/* 通用基础样式（所有设备共享） */
.dialog-mask {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 999;
}

.dialog-container {
  background: #fff;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
}

.dialog-title {
  font-weight: bold;
  text-align: center;
  background: #f9c784;
  color: #fff;
}

.dialog-content {
  text-align: center;
  line-height: 1.2;
  max-height: 60vh;
  overflow-y: auto;
}

.dialog-content a {
  color: #409eff;
  text-decoration: none;
  cursor: pointer;
}

.dialog-content a:hover {
  text-decoration: underline;
}

/* 按钮组通用布局 */
.dialog-btn-group {
  display: flex;
  border-top: 1px solid #eee;
}

.dialog-btn {
  flex: 1; /* 平分宽度 */
  border: none;
  background: transparent;
  cursor: pointer;
  transition: background-color 0.2s ease;
}

/* 取消按钮通用样式 */
.cancel-btn {
  color: #666;
  border-right: 1px solid #eee; /* 分隔线 */
}

/* 确认按钮通用样式 */
.confirm-btn {
  color: #409eff;
}

/* 禁用状态通用样式 */
.dialog-btn:disabled {
  color: #ccc;
  cursor: not-allowed;
  background: transparent;
}

/* -------------------------- 移动端适配 (<=768px) -------------------------- */
@media screen and (max-width: 768px) {
  .dialog-container {
    width: 90%;
    max-width: 400px;
  }

  .dialog-title {
    padding: 16px;
    font-size: 18px;
  }

  .dialog-content {
    padding: 32px 16px;
    font-size: 14px;
  }

  .dialog-btn {
    padding: 12px 0;
    font-size: 16px;
  }

  /* 移动端按钮交互 */
  .dialog-btn:hover {
    background: #f5f7fa;
  }

  .dialog-btn:active {
    background: #e8f4ff;
  }
}

/* -------------------------- 平板适配 (769px ~ 1024px) -------------------------- */
@media screen and (min-width: 769px) and (max-width: 1024px) {
  .dialog-container {
    width: 70%;
    max-width: 500px;
  }

  .dialog-title {
    padding: 18px;
    font-size: 20px;
  }

  .dialog-content {
    padding: 36px 24px;
    font-size: 15px;
    line-height: 1.4;
  }

  .dialog-btn {
    padding: 14px 0;
    font-size: 17px;
  }

  /* 平板按钮hover增强 */
  .dialog-btn:hover {
    background: #f0f5ff;
  }
}

/* -------------------------- PC端适配 (>1024px) -------------------------- */
@media screen and (min-width: 1025px) {
  .dialog-container {
    width: auto;
    max-width: 450px;
    min-width: 300px;
  }

  .dialog-title {
    padding: 20px;
    font-size: 20px;
    background: #f8b768; /* PC端标题背景稍深，提升视觉层次 */
  }

  .dialog-content {
    padding: 40px 32px;
    font-size: 15px;
    line-height: 1.5;
  }

  .dialog-btn {
    padding: 14px 0;
    font-size: 16px;
  }

  /* PC端按钮hover效果增强 */
  .dialog-btn:not(:disabled):hover {
    background: #e8f4ff;
  }

  /* PC端按钮点击效果 */
  .dialog-btn:not(:disabled):active {
    background: #d1eaff;
  }

  /* PC端取消按钮hover单独优化 */
  .cancel-btn:hover {
    color: #444;
  }

  /* PC端确认按钮hover单独优化 */
  .confirm-btn:hover {
    color: #3399ff;
  }
}
</style>